// src/pages/Session.js
import { useParams } from "react-router";
import "./Session.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import Analytics from "../services/APIDeprecated";
import Header from "../components/header/Header";
import { getThumbnail } from "../services/Content";
import { useContent } from "../context/ContentContext";
import AnalyticsHeader from "../components/AnalyticsHeader";
import ContactLink from "../components/header/ContactLink";
import SessionCodeInput from "../components/header/SessionCodeInput";
import PricingLink from "../components/header/PricingLink";
import { usePageMetadata } from "../context/PageMetadataContext";

// Import placeholder images
import Placeholder1 from "../assets/env-placeholder-1.png";
import Placeholder2 from "../assets/env-placeholder-2.png";
import Placeholder3 from "../assets/env-placeholder-3.png";
import Placeholder4 from "../assets/env-placeholder-4.png";
import Placeholder5 from "../assets/env-placeholder-5.png";
import Placeholder6 from "../assets/env-placeholder-6.png";
import Placeholder7 from "../assets/env-placeholder-7.png";
import gradientColor from "../utility/gradientColor";

const PLACEHOLDERS = [
  Placeholder1,
  Placeholder2,
  Placeholder3,
  Placeholder4,
  Placeholder5,
  Placeholder6,
  Placeholder7,
];

function getPlaceholder(id) {
  if (!id) return PLACEHOLDERS[0];
  const hash = id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PLACEHOLDERS[hash % PLACEHOLDERS.length];
}

const POLL_MS = 3000;

function normalizeSessionKey(raw) {
  const s = (raw || "").toString().trim();
  if (!s) return "";
  return s.toLowerCase();
}

function countByLabel(data, label) {
  if (!Array.isArray(data)) return 0;
  return data.filter((d) => d?.label === label).length;
}

function countAssessments(data) {
  if (!Array.isArray(data)) return 0;
  return data.filter((d) => (d?.pk || "").includes("Assessment")).length;
}

function safeDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatClock(ts) {
  const d = safeDate(ts);
  if (!d) return "—";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateRange(start, end) {
  if (!start && !end) return "—";
  const opts = { month: "long", day: "numeric", year: "numeric" };
  const tOpts = { hour: "numeric", minute: "2-digit", hour12: true };
  const s = start
    ? `${start.toLocaleDateString(undefined, opts)} ${start.toLocaleTimeString(undefined, tOpts)}`
    : "—";
  const e = end
    ? `${end.toLocaleDateString(undefined, opts)} ${end.toLocaleTimeString(undefined, tOpts)}`
    : "—";
  return `${s} – ${e}`;
}

function secondsBetween(a, b) {
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 1000);
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const mins = Math.round(seconds / 60);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function safeString(v) {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
}

function safeJsonParse(v) {
  try {
    if (v == null) return null;
    if (typeof v === "object") return v;
    return JSON.parse(String(v));
  } catch {
    return null;
  }
}

function getEventType(pk) {
  const s = safeString(pk);
  if (!s) return "";
  return s.split("#")[0] || "";
}

function shouldRenderInTimeline(ev) {
  const type = getEventType(ev?.pk);
  return type === "EnvironmentVisited" || type === "ElementExpanded" || type === "Assessment";
}

function scoreBubbleProps(score, isBlank) {
  if (isBlank) {
    return { className: "sessionScoreBubble sessionToneBlank", style: {} };
  }
  return {
    className: "sessionScoreBubble",
    style: { background: gradientColor(Number(score || 0)) },
  };
}

export default function Session({ dashboard }) {
  const { sessionId: rawSessionId } = useParams();
  const sessionKey = useMemo(() => normalizeSessionKey(rawSessionId), [rawSessionId]);
  const { getItem } = useContent();

  const [sessionData, setSessionData] = useState(null);
  const [experience, setExperience] = useState("Unknown Experience");
  const [isPolling, setIsPolling] = useState(false);

  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [descriptionsRead, setDescriptionsRead] = useState(0);
  const [insightsCompleted, setInsightsCompleted] = useState(0);

  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const cancelledRef = useRef(false);

  useEffect(() => {
    setShowTitleInHeader(true);
    setTitle("Session");
    return () => {
      cancelledRef.current = true;
    };
  }, [setShowTitleInHeader, setTitle]);

  useEffect(() => {
    if (experience) setTitle(`Session - ${experience}`);
  }, [experience, setTitle]);

  const computeStats = (data) => {
    setEnvironmentsVisited(countByLabel(data, "EnvironmentVisited"));
    setDescriptionsRead(countByLabel(data, "ElementExpanded"));
    setInsightsCompleted(countAssessments(data));
  };

  const loadExperienceName = async (data) => {
    try {
      const systemId = Array.isArray(data) ? data?.[0]?.systemId : null;
      if (!systemId) {
        if (!cancelledRef.current) setExperience("Unknown Experience");
        return;
      }
      const res = await getItem("systems", systemId);
      const name =
        res && typeof res.name === "string" && res.name.trim()
          ? res.name.trim()
          : "Unknown Experience";
      if (!cancelledRef.current) setExperience(name);
    } catch {
      if (!cancelledRef.current) setExperience("Unknown Experience");
    }
  };

  const fetchSession = async () => {
    if (!sessionKey) {
      setSessionData(null);
      setNotFound(true);
      setFetchError(null);
      computeStats(null);
      setExperience("Unknown Experience");
      return;
    }

    try {
      setFetchError(null);
      setNotFound(false);

      try {
        const res = await Analytics.get(`sessioncode/${encodeURIComponent(sessionKey)}`);
        const data = res?.data;

        if (!data)
          throw Object.assign(new Error("Empty sessioncode response"), { _empty: true });
        if (cancelledRef.current) return;

        setSessionData(data);
        computeStats(data);
        loadExperienceName(data);
        return;
      } catch (e1) {
        const status = e1?.response?.status;
        const is404 = status === 404;
        const isEmpty = e1?._empty === true;
        if (!is404 && !isEmpty) throw e1;
      }

      const res2 = await Analytics.get(`usercode/${encodeURIComponent(sessionKey)}`);
      const data2 = res2?.data;

      if (cancelledRef.current) return;

      if (!data2) {
        setSessionData(null);
        setNotFound(true);
        computeStats(null);
        setExperience("Unknown Experience");
        return;
      }

      setSessionData(data2);
      computeStats(data2);
      loadExperienceName(data2);
    } catch (e) {
      if (cancelledRef.current) return;

      const status = e?.response?.status;
      if (status === 404) {
        setSessionData(null);
        setNotFound(true);
        setFetchError(null);
        computeStats(null);
        setExperience("Unknown Experience");
        return;
      }

      setSessionData(null);
      setNotFound(false);
      setFetchError(e);
      computeStats(null);
      setExperience("Unknown Experience");
    }
  };

  useEffect(() => {
    cancelledRef.current = false;

    let intervalId = null;
    fetchSession();

    if (isPolling) intervalId = setInterval(fetchSession, POLL_MS);

    return () => {
      cancelledRef.current = true;
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey, isPolling]);

  const hasData = Array.isArray(sessionData) && sessionData.length > 0;
  const rawDisplay = (rawSessionId || "").toString().trim();

  const { startAt, endAt, durationLabel } = useMemo(() => {
    if (!hasData) return { startAt: null, endAt: null, durationLabel: "—" };

    const dates = sessionData
      .map((e) => safeDate(e?.sk))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());

    const start = dates[0] || null;
    const end = dates[dates.length - 1] || null;
    const seconds = secondsBetween(start, end);

    return { startAt: start, endAt: end, durationLabel: formatDuration(seconds) };
  }, [hasData, sessionData]);

  // Filter events that should render
  const timelineEvents = useMemo(() => {
    if (!hasData) return [];
    return sessionData.filter(shouldRenderInTimeline);
  }, [hasData, sessionData]);

  return (
    <div className="sessionPage">
      {!dashboard && (
        <Header>
          <PricingLink />
          <ContactLink />
          <SessionCodeInput />
        </Header>
      )}

      <div className="sessionWrap">
        {/* Header Section */}
        <div className="sessionHeader">
          <div className="sessionHeaderLeft">
            <h1 className="sessionTitle">{experience || "Unknown Experience"}</h1>
            <div className="sessionMeta">
              <span className="sessionCode">{rawDisplay || "—"}</span>
              <span className="sessionMetaDot">·</span>
              <span className="sessionDateRange">{formatDateRange(startAt, endAt)}</span>
            </div>
          </div>

          <div className="sessionHeaderRight">
            <div className="sessionDurationPill">{durationLabel}</div>
            <AnalyticsHeader
              experience={experience}
              isPolling={isPolling}
              setIsPolling={setIsPolling}
            />
          </div>
        </div>

        {notFound && (
          <div className="sessionNotice">
            Session not found (code: <b>{rawDisplay || "—"}</b>).
          </div>
        )}

        {fetchError && !notFound && (
          <div className="sessionError">Couldn't load this session.</div>
        )}

        {/* KPI Cards */}
        <div className="sessionKpis">
          <div className="kpiCard">
            <div className="kpiValue">{environmentsVisited}</div>
            <div className="kpiLabel">Environments Visited</div>
          </div>
          <div className="kpiCard">
            <div className="kpiValue">{descriptionsRead}</div>
            <div className="kpiLabel">Descriptions Read</div>
          </div>
          <div className="kpiCard">
            <div className="kpiValue">{insightsCompleted}</div>
            <div className="kpiLabel">Insights Completed</div>
          </div>
        </div>

        {/* Timeline */}
        <div className="sessionTimeline">
          {timelineEvents.length === 0 && hasData && (
            <div className="sessionTimelineEmpty">No timeline events to display.</div>
          )}

          {timelineEvents.map((ev, idx) => (
            <TimelineEvent key={`${ev?.sk || "sk"}-${ev?.pk || "pk"}-${idx}`} data={ev} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Timeline Event Component
function TimelineEvent({ data }) {
  const type = getEventType(data?.pk);
  const { getEnvironmentSync, getItem } = useContent();
  
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [thumb, setThumb] = useState(null);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      setTitle("");
      setSubtitle("");
      setThumb(null);
      setThumbError(false);

      if (type === "EnvironmentVisited") {
        setTitle("Navigated to");
        const environmentThumb = await getThumbnail(data?.value);
        if (!cancelled) setThumb(environmentThumb || null);

        // OPTIMIZATION: Use synchronous lookup from ContentContext first
        const env = getEnvironmentSync(data?.value);
        if (env?.name) {
          setSubtitle(`Environment: ${env.name}`);
          return;
        }

        // Fallback to API call if not in cache
        try {
          const res = await getItem("environments", data?.value, false);
          const name = safeString(res?.name) || "Unknown environment";
          if (!cancelled) setSubtitle(`Environment: ${name}`);
        } catch {
          if (!cancelled) setSubtitle("Environment: Unknown environment");
        }
        return;
      }

      if (type === "ElementExpanded") {
        setTitle("Read Description");
        const elementThumb = await getThumbnail(data?.value);
        if (!cancelled) setThumb(elementThumb || null);

        try {
          const res = await getItem("elements", data?.value, false);
          const rawName = safeString(res?.name);
          const cleaned = rawName ? rawName.replaceAll("_", " ") : "Unknown element";
          if (!cancelled) setSubtitle(`Element: ${cleaned}`);
        } catch {
          if (!cancelled) setSubtitle("Element: Unknown element");
        }
        return;
      }

      if (type === "Assessment") {
        setTitle("Completed Assessment");
        return;
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [type, data, getEnvironmentSync, getItem]);

  const isAssessment = type === "Assessment";
  const placeholder = getPlaceholder(data?.value);

  const handleImageError = () => {
    setThumbError(true);
  };

  if (isAssessment) {
    return <AssessmentEvent data={data} />;
  }

  return (
    <div className="tlRow">
      <div className="tlTime">{formatClock(data?.sk)}</div>

      <div className="tlIconCol">
        <div className="tlIcon">
          {thumb && !thumbError ? (
            <img src={thumb} alt="" onError={handleImageError} />
          ) : (
            <img src={placeholder} alt="" />
          )}
        </div>
        <div className="tlConnector" />
      </div>

      <div className="tlContent">
        <div className="tlTitle">{title}</div>
        <div className="tlSubtitle">{subtitle}</div>
      </div>
    </div>
  );
}

// Assessment Event Component
function AssessmentEvent({ data }) {
  const parsed = safeJsonParse(data?.value);
  const question = safeString(parsed?.text) || "—";
  const answerRaw = safeString(data?.transcript).trim();
  const hasAnswer = Boolean(answerRaw);
  const feedback = safeString(data?.student_evaluation).trim();
  const summary = safeString(data?.summary).trim();
  const score = data?.score;
  const scoreBubble = scoreBubbleProps(score, !hasAnswer);

  return (
    <div className="tlRow tlRowAssessment">
      <div className="tlTime">{formatClock(data?.sk)}</div>

      <div className="tlIconCol">
        <div className="tlIcon tlIconQuestion">
          <span>❓</span>
        </div>
        <div className="tlConnector" />
      </div>

      <div className="tlAssessmentContent">
        <div className="tlAssessmentTitle">Completed Assessment</div>
        
        <div className="tlAssessmentGrid">
          <div className="tlAssessmentLeft">
            <div className="tlAssessmentBlock">
              <div className="tlAssessmentLabel">Question</div>
              <div className="tlAssessmentText">{question}</div>
            </div>

            <div className="tlAssessmentBlock">
              <div className="tlAssessmentLabel">Answer</div>
              <div className={`tlAssessmentText ${!hasAnswer ? "isBlank" : ""}`}>
                {hasAnswer ? answerRaw : "No response provided"}
              </div>
            </div>
          </div>

          <div className="tlAssessmentScore">
          <div className={scoreBubble.className} style={scoreBubble.style}>
            {!hasAnswer ? "—" : `${Math.round(Number(score || 0))}%`}
          </div>
          </div>

          <div className="tlAssessmentRight">
            {feedback && (
              <div className="tlAssessmentBlock">
                <div className="tlAssessmentLabel">AI Feedback</div>
                <div className="tlAssessmentText">{feedback}</div>
              </div>
            )}

            {summary && (
              <div className="tlAssessmentBlock">
                <div className="tlAssessmentLabel">Summary</div>
                <div className="tlAssessmentText">{summary}</div>
              </div>
            )}

            {!feedback && !summary && (
              <div className="tlAssessmentEmpty">No feedback available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
